<template>
  <div class="page-container">
    <div class="sidebar">
      <Sidebar />
    </div>
    <div class="content-wrapper">
      <!-- Error Alert -->
      <div v-if="error" class="error-alert">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
        <button class="close-alert" @click="error = null">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Search and Actions Header -->
      <div class="header-actions">
        <div class="search-container">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search instructors..."
            class="search-input"
            @input="handleSearch"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading instructors...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="instructors-content">
        <!-- Stats Card -->
        <div class="quick-stats">
          <div class="stat-card">
            <div class="stat-icon total">
              <i class="fas fa-users"></i>
            </div>
            <div class="stat-content">
              <h4>Total Instructors</h4>
              <div class="stat-value">{{ instructors.length }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon keywords">
              <i class="fas fa-tags"></i>
            </div>
            <div class="stat-content">
              <h4>Instructors with Keywords</h4>
              <div class="stat-value">{{ instructorsWithKeywords }}</div>
            </div>
          </div>
        </div>

        <!-- Instructors Grid -->
        <div class="instructors-grid">
          <div 
            v-for="instructor in instructors" 
            :key="instructor.id" 
            class="instructor-card"
            @click="selectInstructor(instructor)"
          >
            <div class="instructor-image">
              <img :src="instructor.img_url || 'https://via.placeholder.com/150'" :alt="instructor.first_name" />
            </div>
            <div class="instructor-info">
              <h3>{{ instructor.first_name }} {{ instructor.last_name }}</h3>
              <p class="instructor-expertise">{{ instructor.expertise || 'No expertise listed' }}</p>
              <div class="instructor-rating">
                <i class="fas fa-star"></i>
                <span>{{ instructor.rating }} ({{ instructor.total_reviews }} reviews)</span>
              </div>
              <div class="instructor-price">
                <i class="fas fa-dollar-sign"></i>
                <span>{{ instructor.price_per_session }} per session</span>
              </div>
              <button class="view-keywords-btn" @click.stop="viewInstructorKeywords(instructor)">
                <i class="fas fa-tags"></i> View Keywords
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Instructor Keywords Modal -->
      <div v-if="showKeywordsModal" class="modal-overlay">
        <div class="modal-container keywords-modal">
          <div class="modal-header">
            <h3>Keywords for {{ selectedInstructor ? `${selectedInstructor.first_name} ${selectedInstructor.last_name}` : '' }}</h3>
            <button class="close-modal" @click="closeKeywordsModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="keywordsLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Loading keywords...</p>
            </div>
            <div v-else>
              <div v-if="instructorKeywords.length === 0" class="no-keywords">
                <i class="fas fa-info-circle"></i>
                <p>No keywords assigned to this instructor.</p>
              </div>
              <div v-else class="keywords-list">
                <div v-for="keyword in instructorKeywords" :key="keyword.id" class="keyword-item">
                  <i class="fas fa-tag"></i>
                  <span>{{ keyword.keyword }}</span>
                  <button class="remove-keyword" @click="removeKeyword(keyword)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <div class="keywords-assign-section">
                <h4>Assign Keywords</h4>
                <div class="keywords-selection">
                  <select v-model="selectedKeywordId" class="keyword-select">
                    <option value="">Select a keyword</option>
                    <option 
                      v-for="keyword in availableKeywords" 
                      :key="keyword.id" 
                      :value="keyword.id"
                    >
                      {{ keyword.keyword }}
                    </option>
                  </select>
                  <button 
                    class="add-keyword-btn" 
                    :disabled="!selectedKeywordId" 
                    @click="addKeyword"
                  >
                    <i class="fas fa-plus"></i> Add
                  </button>
                </div>
              </div>

              <div class="save-keywords-section">
                <button 
                  class="save-keywords-btn" 
                  @click="updateInstructorKeywords"
                >
                  <i class="fas fa-save"></i> Save Keywords
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'InstructorKeywords',
  components: {
    Sidebar
  },
  data() {
    return {
      instructors: [],
      allKeywords: [],
      loading: true,
      keywordsLoading: false,
      searchQuery: '',
      searchTimeout: null,
      error: null,
      showKeywordsModal: false,
      selectedInstructor: null,
      instructorKeywords: [],
      selectedKeywordId: '',
      selectedKeywordIds: [], // To track changes in keywords
      instructorsWithKeywordsCount: 0
    };
  },
  computed: {
    availableKeywords() {
      // Filter out keywords already assigned to the instructor
      if (!this.allKeywords.length || !this.instructorKeywords.length) {
        return this.allKeywords;
      }
      
      const assignedIds = this.instructorKeywords.map(k => k.id);
      return this.allKeywords.filter(k => !assignedIds.includes(k.id));
    },
    instructorsWithKeywords() {
      return this.instructorsWithKeywordsCount;
    }
  },
  created() {
    this.fetchInstructors();
    this.fetchAllKeywords();
    this.fetchInstructorsWithKeywordsCount();
  },
  methods: {
    async fetchInstructors() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          `https://api.kewaar.com/api/admin/instructors1`, 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data.success) {
          this.instructors = response.data.data.filter(instructor => 
            instructor.first_name && instructor.last_name
          );
        } else {
          this.error = 'Failed to fetch instructors: ' + response.data.message;
        }
      } catch (error) {
        this.error = 'Failed to fetch instructors. Please try again.';
        console.error('Error fetching instructors:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async fetchAllKeywords() {
      try {
        const token = localStorage.getItem('token') || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c';
        
        const response = await axios.get(
          `https://api.kewaar.com/api/admin/keywords?per_page=100`, 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data && response.data.data) {
          this.allKeywords = response.data.data.data || [];
        } else {
          console.error('Unexpected keywords response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching all keywords:', error);
      }
    },
    
    async fetchInstructorsWithKeywordsCount() {
      try {
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          `https://api.kewaar.com/api/admin/instructors/with-keywords/count`, 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data && response.data.success) {
          this.instructorsWithKeywordsCount = response.data.count || 0;
        } else {
          // Fallback if the endpoint doesn't exist or doesn't return expected data
          this.instructorsWithKeywordsCount = 0;
          console.warn('Unable to fetch instructors with keywords count');
        }
      } catch (error) {
        console.error('Error fetching instructors with keywords count:', error);
        // Implement a fallback count calculation (this is less efficient)
        this.calculateInstructorsWithKeywordsCount();
      }
    },
    
    async calculateInstructorsWithKeywordsCount() {
      // Fallback method if the API endpoint doesn't exist
      try {
        let count = 0;
        const token = localStorage.getItem('token');
        
        // This is not efficient, but it's a fallback
        for (const instructor of this.instructors) {
          const response = await axios.get(
            `https://api.kewaar.com/api/admin/instructors/${instructor.id}/keywords`, 
            {
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          if (response.data.status === 'success' && 
              response.data.data.keywords && 
              response.data.data.keywords.length > 0) {
            count++;
          }
        }
        
        this.instructorsWithKeywordsCount = count;
      } catch (error) {
        console.error('Error calculating instructors with keywords count:', error);
        this.instructorsWithKeywordsCount = 0;
      }
    },
    
    handleSearch() {
      // Implement search functionality here
      // This would filter instructors based on searchQuery
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // For now, we'll just do a simple client-side filtering
        // In a real app, you might want to call the API with search params
        this.fetchInstructors();
      }, 300);
    },
    
    selectInstructor(instructor) {
      this.selectedInstructor = instructor;
    },
    
    async viewInstructorKeywords(instructor) {
      this.selectedInstructor = instructor;
      this.showKeywordsModal = true;
      await this.fetchInstructorKeywords();
    },
    
    async fetchInstructorKeywords() {
      if (!this.selectedInstructor) return;
      
      try {
        this.keywordsLoading = true;
        this.error = null;
        const token = localStorage.getItem('token') || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c';
        
        const response = await axios.get(
          `https://api.kewaar.com/api/admin/instructors/${this.selectedInstructor.id}/keywords`, 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data.status === 'success') {
          this.instructorKeywords = response.data.data.keywords || [];
          this.selectedKeywordIds = this.instructorKeywords.map(k => k.id);
        } else {
          this.error = 'Failed to fetch instructor keywords.';
          this.instructorKeywords = [];
        }
      } catch (error) {
        this.error = 'Failed to fetch instructor keywords. Please try again.';
        console.error('Error fetching instructor keywords:', error);
        this.instructorKeywords = [];
      } finally {
        this.keywordsLoading = false;
      }
    },
    
    closeKeywordsModal() {
      this.showKeywordsModal = false;
      this.selectedInstructor = null;
      this.instructorKeywords = [];
      this.selectedKeywordId = '';
      this.selectedKeywordIds = [];
    },
    
    addKeyword() {
      if (!this.selectedKeywordId) return;
      
      const keywordToAdd = this.allKeywords.find(k => k.id === this.selectedKeywordId);
      if (keywordToAdd) {
        // Add to the displayed keywords
        this.instructorKeywords.push(keywordToAdd);
        // Add to the tracked IDs
        this.selectedKeywordIds.push(keywordToAdd.id);
        // Reset selection
        this.selectedKeywordId = '';
      }
    },
    
    removeKeyword(keyword) {
      // Remove from displayed keywords
      this.instructorKeywords = this.instructorKeywords.filter(k => k.id !== keyword.id);
      // Remove from tracked IDs
      this.selectedKeywordIds = this.selectedKeywordIds.filter(id => id !== keyword.id);
    },
    
    async updateInstructorKeywords() {
      if (!this.selectedInstructor) return;
      
      try {
        this.error = null;
        const token = localStorage.getItem('token');
      
        const payload = {
          keywords: this.selectedKeywordIds
        };
        
        console.log('Sending payload:', payload);
        
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/instructors/${this.selectedInstructor.id}/keywords`,
          payload,
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data.status === 'success') {
          // Refresh the keywords display
          await this.fetchInstructorKeywords();
          // Refresh the count of instructors with keywords
          await this.fetchInstructorsWithKeywordsCount();
          alert('Keywords updated successfully!');
        } else {
          this.error = 'Failed to update instructor keywords: ' + (response.data.message || 'Unknown error');
          console.error('API Error:', response.data);
        }
      } catch (error) {
        this.error = 'Failed to update instructor keywords. Please try again.';
        console.error('Error updating instructor keywords:', error);
        
        // Better error reporting
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          
          // If there are validation errors in the response, display them
          if (error.response.data && error.response.data.errors) {
            const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
            this.error = `Validation errors: ${errorMessages}`;
          }
        }
      }
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  height: 100vh;
}

.sidebar {
  width: 250px;
  height: 100%;
}

.content-wrapper {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #f5f7fa;
  position: relative;
}

.error-alert {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.close-alert {
  margin-left: auto;
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.search-input {
  width: 100%;
  padding: 10px 10px 10px 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3b82f6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.instructors-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.quick-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  font-size: 20px;
}

.stat-icon.total {
  background-color: #dbeafe;
  color: #3b82f6;
}

.stat-icon.keywords {
  background-color: #fef3c7;
  color: #f59e0b;
}

.stat-content h4 {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 4px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

/* Instructor Grid Styles */
.instructors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.instructor-card {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.instructor-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.instructor-image {
  height: 200px;
  overflow: hidden;
}

.instructor-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.instructor-info {
  padding: 16px;
}

.instructor-info h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #1f2937;
}

.instructor-expertise {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
}

.instructor-rating, .instructor-price {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
}

.instructor-rating i {
  color: #f59e0b;
}

.view-keywords-btn {
  margin-top: 12px;
  width: 100%;
  padding: 8px 0;
  border: none;
  background-color: #3b82f6;
  color: white;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.view-keywords-btn:hover {
  background-color: #2563eb;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 600px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-modal {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #6b7280;
}

.modal-body {
  padding: 20px;
}

.no-keywords {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px;
  color: #6b7280;
}

.no-keywords i {
  font-size: 24px;
  color: #3b82f6;
}

.keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.keyword-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f3f4f6;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 14px;
}

.keyword-item i {
  color: #3b82f6;
}

.remove-keyword {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 14px;
}

.keywords-assign-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.keywords-assign-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #1f2937;
}

.keywords-selection {
  display: flex;
  gap: 10px;
}

.keyword-select {
  flex: 1;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.add-keyword-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-keyword-btn:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.save-keywords-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.save-keywords-btn {
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.save-keywords-btn:hover {
  background-color: #059669;
}
</style> 